import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

/**
 * The Publifying logo, in one place.
 *
 * Every surface that shows the brand goes through this component, so replacing
 * the artwork means dropping one file into `public/brand/` — no hunting through
 * screens for stray copies.
 *
 * It loads in three stages, each one covering the last:
 *
 *   1. `/brand/publifying-logo.png` — the self-hosted copy. Fastest: same
 *      origin, already-warm connection, no extra DNS or TLS handshake in front
 *      of the login screen.
 *   2. the marketing site's copy — so the logo is correct the moment this code
 *      deploys, even before anyone has copied the file across.
 *   3. a typographic wordmark — so a network failure or a mistyped path shows
 *      the brand name rather than a broken-image icon.
 *
 * To self-host it (worth doing, it removes a cross-origin request from the
 * first paint of the login page):
 *
 *   curl -o frontend/public/brand/publifying-logo.png \
 *     https://publifying.com/fig/Publifying-Logo-Final.png
 */

const REMOTE = 'https://publifying.com/fig/Publifying-Logo-Final.png';

const SRC = {
  full: ['/brand/publifying-logo.png', REMOTE],
  // The square mark now exists — the same 1024x1024 icon the app stores and
  // the browser tab use, so every square slot shows one image rather than a
  // letterboxed wordmark. The wordmark stays as the fallback beneath it.
  mark: ['/brand/publifying-mark.png', '/brand/publifying-logo.png', REMOTE],
};

/**
 * Force any artwork to solid white or solid black.
 *
 * `brightness(0)` crushes every colour to black regardless of what it started
 * as, and `invert(1)` then flips that to white — so one rule handles a colour
 * logo, a black logo and a white one identically. That is what makes the mark
 * usable on the dark navy rail without shipping a second file.
 */
const TONE_FILTER = {
  color: 'none',
  light: 'brightness(0) invert(1)',
  dark: 'brightness(0)',
};

export default function BrandLogo({
  variant = 'full',
  height = 32,
  tone = 'color',
  /** Show the "Publifying" wordmark beside a `mark` variant. */
  withWordmark = false,
  sx = {},
  ...rest
}) {
  // Which source in the chain we're currently trying. Each failure advances it;
  // running off the end drops to the typographic fallback below.
  const sources = SRC[variant] || SRC.full;
  const [attempt, setAttempt] = useState(0);
  const failed = attempt >= sources.length;

  const wordmarkColor = tone === 'light' ? '#FFFFFF' : '#141C46';

  /* --- Fallback: every source failed ------------------------------------- */
  if (failed) {
    return (
      <Stack direction="row" alignItems="center" spacing={1.25} sx={sx} {...rest}>
        <Box
          sx={{
            width: height, height, borderRadius: height / 3.2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg,#6C7BFF,#8B5CF6)',
            color: '#fff', fontWeight: 800, fontSize: height * 0.5, lineHeight: 1,
          }}
        >
          P
        </Box>
        {(variant === 'full' || withWordmark) && (
          <Typography
            sx={{
              color: wordmarkColor,
              fontWeight: 800,
              fontSize: height * 0.62,
              letterSpacing: '-.01em',
              lineHeight: 1,
            }}
          >
            Publifying
          </Typography>
        )}
      </Stack>
    );
  }

  const image = (
    <Box
      component="img"
      // `key` forces a fresh <img> when the source changes. Without it the
      // browser keeps the errored element and never re-requests.
      key={attempt}
      src={sources[attempt]}
      alt="Publifying"
      onError={() => setAttempt((n) => n + 1)}
      // Decode off the main thread and don't let the mark be dragged out of
      // the sidebar as a stray image.
      decoding="async"
      draggable={false}
      sx={{
        height,
        width: 'auto',
        // Square marks get a square box; a wide lockup keeps its own ratio.
        maxWidth: variant === 'mark' ? height : '100%',
        display: 'block',
        objectFit: 'contain',
        filter: TONE_FILTER[tone] || 'none',
        userSelect: 'none',
        ...(variant === 'full' && !withWordmark ? sx : {}),
      }}
    />
  );

  if (variant === 'full' && !withWordmark) {
    return <Box sx={{ display: 'inline-flex', ...sx }} {...rest}>{image}</Box>;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={sx} {...rest}>
      {image}
      {withWordmark && (
        <Typography
          sx={{
            color: wordmarkColor,
            fontWeight: 800,
            fontSize: height * 0.62,
            letterSpacing: '-.01em',
            lineHeight: 1,
          }}
        >
          Publifying
        </Typography>
      )}
    </Stack>
  );
}
